import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Button, ButtonGroup, LinearProgress, Paper } from '@material-ui/core';
import { DeleteOutline } from '@material-ui/icons';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { FilesData } from 'services/dataService';
import { toSpotifyEmbedUrl } from 'helpers/newsletter';
import { useStyles } from './styles';

const Quill = ReactQuill.Quill;
const BlockEmbed = Quill.import('blots/block/embed');
const Video = Quill.import('formats/video');

// Embed de vídeo/áudio: além do YouTube (convertido pelo próprio Quill), aceita links do Spotify.
class EmbedVideo extends Video {
  static sanitize(url) {
    return toSpotifyEmbedUrl(url) || super.sanitize(url);
  }
}
Quill.register(EmbedVideo, true);

class VideoFile extends BlockEmbed {
  static create(value) {
    const node = super.create(value);
    node.setAttribute('src', value);
    node.setAttribute('controls', '');
    node.setAttribute('controlsList', 'nodownload');
    node.setAttribute('preload', 'metadata');
    return node;
  }

  static value(node) {
    return node.getAttribute('src');
  }
}
VideoFile.blotName = 'videoFile';
VideoFile.tagName = 'VIDEO';
VideoFile.className = 'ql-video-file';
Quill.register(VideoFile, true);

const icons = Quill.import('ui/icons');
icons.videoFile =
  '<svg viewBox="0 0 18 18"><path class="ql-fill" d="M9 2.5l3.5 3.5h-2.5v4h-2v-4h-2.5z"/><rect class="ql-fill" x="3" y="12" width="12" height="2"/><rect class="ql-fill" x="3" y="9" width="2" height="4"/><rect class="ql-fill" x="13" y="9" width="2" height="4"/></svg>';

const formats = [
  'header',
  'bold',
  'italic',
  'underline',
  'strike',
  'blockquote',
  'list',
  'bullet',
  'indent',
  'align',
  'link',
  'image',
  'width',
  'video',
  'videoFile',
];

const imageSizes = [
  { key: 'small', width: '25%' },
  { key: 'medium', width: '50%' },
  { key: 'large', width: '75%' },
  { key: 'full', width: '100%' },
];

const mediaTags = ['IMG', 'IFRAME', 'VIDEO'];

const toolbarTitles = {
  '.ql-header': 'header',
  '.ql-bold': 'bold',
  '.ql-italic': 'italic',
  '.ql-underline': 'underline',
  '.ql-strike': 'strike',
  '.ql-blockquote': 'blockquote',
  '.ql-list[value="ordered"]': 'list_ordered',
  '.ql-list[value="bullet"]': 'list_bullet',
  '.ql-indent[value="-1"]': 'indent_decrease',
  '.ql-indent[value="+1"]': 'indent_increase',
  '.ql-align': 'align',
  '.ql-link': 'link',
  '.ql-image': 'image',
  '.ql-video': 'video',
  '.ql-videoFile': 'video_file',
  '.ql-clean': 'clean',
};

export default ({ value, onChange }) => {
  const classes = useStyles();
  const { t } = useTranslation(['newsletter', 'toastify']);
  const quillRef = useRef();
  const imageInputRef = useRef();
  const videoInputRef = useRef();
  const rootRef = useRef();
  const selectedMediaRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [mediaBar, setMediaBar] = useState(null);

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, false] }],
          ['bold', 'italic', 'underline', 'strike', 'blockquote'],
          [
            { list: 'ordered' },
            { list: 'bullet' },
            { indent: '-1' },
            { indent: '+1' },
          ],
          [{ align: [] }],
          ['link', 'image', 'video', 'videoFile'],
          ['clean'],
        ],
        handlers: {
          image: () => imageInputRef.current.click(),
          videoFile: () => videoInputRef.current.click(),
        },
      },
    }),
    []
  );

  useEffect(() => {
    const toolbar = quillRef.current.getEditor().getModule('toolbar').container;

    Object.entries(toolbarTitles).forEach(([selector, key]) => {
      const title = t(`newsletter:toolbar.${key}`);
      toolbar.querySelectorAll(selector).forEach((control) => {
        control.setAttribute('title', title);
        control.setAttribute('aria-label', title);
      });
    });
  }, [t]);

  const showMediaBar = (media) => {
    const rootBounds = rootRef.current.getBoundingClientRect();
    const mediaBounds = media.getBoundingClientRect();

    selectedMediaRef.current = media;
    setMediaBar({
      top: mediaBounds.top - rootBounds.top + 8,
      left: mediaBounds.left - rootBounds.left + 8,
      isImage: media.tagName === 'IMG',
      width: media.getAttribute('width'),
    });
  };

  const hideMediaBar = () => {
    selectedMediaRef.current = null;
    setMediaBar(null);
  };

  useEffect(() => {
    const { root } = quillRef.current.getEditor();

    const handleMouseOver = (event) => {
      if (mediaTags.includes(event.target.tagName)) {
        if (selectedMediaRef.current !== event.target) {
          showMediaBar(event.target);
        }
      } else {
        hideMediaBar();
      }
    };

    root.addEventListener('mouseover', handleMouseOver);
    root.addEventListener('keydown', hideMediaBar);

    return () => {
      root.removeEventListener('mouseover', handleMouseOver);
      root.removeEventListener('keydown', hideMediaBar);
    };
  }, []);

  const handleImageSize = (width) => {
    const editor = quillRef.current.getEditor();
    const image = selectedMediaRef.current;
    const blot = image && Quill.find(image);

    if (!blot) {
      hideMediaBar();
      return;
    }

    editor.formatText(editor.getIndex(blot), 1, 'width', width, 'user');
    window.requestAnimationFrame(() => {
      if (selectedMediaRef.current === image && rootRef.current) {
        showMediaBar(image);
      }
    });
  };

  const handleRemoveMedia = () => {
    const editor = quillRef.current.getEditor();
    const media = selectedMediaRef.current;
    const blot = media && Quill.find(media);

    if (blot) editor.deleteText(editor.getIndex(blot), 1, 'user');
    hideMediaBar();
  };

  const handleUpload = async (event, format, context) => {
    const file = event.target.files[0];
    event.target.value = '';
    if (!file) return;

    const editor = quillRef.current.getEditor();

    try {
      setUploading(true);
      const response = await FilesData.addFile(file, `Newsletter/${context}`);
      const range = editor.getSelection(true);
      editor.insertEmbed(range.index, format, response.data.url, 'user');
      editor.setSelection(range.index + 1, 0);
    } catch (err) {
      toast.error(t('newsletter:toast.upload_error'));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className={classes.root} ref={rootRef} onMouseLeave={hideMediaBar}>
      {mediaBar && (
        <Paper
          elevation={3}
          className={classes.mediaBar}
          style={{ top: mediaBar.top, left: mediaBar.left }}
        >
          {mediaBar.isImage && (
            <ButtonGroup size="small" color="primary">
              {imageSizes.map((size) => (
                <Button
                  key={size.key}
                  variant={
                    mediaBar.width === size.width ? 'contained' : 'outlined'
                  }
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => handleImageSize(size.width)}
                >
                  {t(`newsletter:image_size.${size.key}`)}
                </Button>
              ))}
            </ButtonGroup>
          )}
          <Button
            size="small"
            variant="outlined"
            startIcon={<DeleteOutline />}
            onMouseDown={(event) => event.preventDefault()}
            onClick={handleRemoveMedia}
          >
            {t('newsletter:media.remove')}
          </Button>
        </Paper>
      )}
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value}
        onChange={onChange}
        modules={modules}
        formats={formats}
        bounds={`.${classes.root}`}
        placeholder={t('newsletter:form.content_placeholder')}
      />
      {uploading && <LinearProgress />}
      <input
        ref={imageInputRef}
        type="file"
        hidden
        accept="image/png, image/jpeg, image/gif"
        onChange={(event) => handleUpload(event, 'image', 'Images')}
      />
      <input
        ref={videoInputRef}
        type="file"
        hidden
        accept="video/mp4, video/webm"
        onChange={(event) => handleUpload(event, 'videoFile', 'Videos')}
      />
    </div>
  );
};
